import React, { useEffect, useState, useRef, useCallback } from 'react';
import ForceGraph2D, { ForceGraphMethods, NodeObject } from 'react-force-graph-2d';
import { useDarkMode } from '../../context/DarkModeContext';
import { goodThingsService } from '../../services/goodThings';
import { GoodThing } from '../../types/good-things';
import { Card } from '../../components/ui/card';
import { 
  Search, 
  Share2, 
  Zap,
  Minimize2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { GoodThingCard } from '../../components/good-things/GoodThingCard';
import { ScrollArea } from '../../components/ui/scroll-area';
import { TooltipProvider } from '../../components/ui/tooltip';

interface Node {
  id: string;
  name: string;
  val: number; // Size based on connections
  sent: number;
  received: number;
  color?: string;
  x?: number;
  y?: number;
}

interface Link {
  source: string | Node;
  target: string | Node;
  value: number; // Strength based on number of interactions
}

interface GraphData {
  nodes: Node[];
  links: Link[];
}

const GoodThingsNetwork = () => {
  const { isDarkMode } = useDarkMode();
  const [data, setData] = useState<GraphData>({ nodes: [], links: [] });
  const [rawEntries, setRawEntries] = useState<GoodThing[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [highlightNodes, setHighlightNodes] = useState(new Set<string>());
  const [highlightLinks, setHighlightLinks] = useState(new Set<string>());
  const graphRef = useRef<ForceGraphMethods<NodeObject<Node>, Link>>();
  const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Dialog State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<'sent' | 'received'>('received');
  const [dialogEntries, setDialogEntries] = useState<GoodThing[]>([]);

  // Colors
  const NODE_COLOR = isDarkMode ? '#ec4899' : '#db2777'; // Pink-500/600
  const SELECTED_COLOR = '#d97706'; // Amber-600 (Dark Yellow/Gold)
  const CONNECTED_COLOR = '#fbbf24'; // Amber-400
  const TEXT_COLOR = isDarkMode ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)';
  const TEXT_BG_COLOR = isDarkMode ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.8)';

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const entries = await goodThingsService.getAllEntries();
        setRawEntries(entries);
        processGraphData(entries);
      } catch (error) {
        console.error('Error fetching Good Things:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setContainerDimensions({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight
        });
      }
    };

    window.addEventListener('resize', updateDimensions);
    updateDimensions();

    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  useEffect(() => {
    if (graphRef.current) {
      // Configure physics for a "Map-like" feel
      // High repulsion to spread nodes out and avoid overlap
      graphRef.current.d3Force('charge')?.strength(-800);
      
      // Longer links to create breathing room
      graphRef.current.d3Force('link')?.distance(100);
      
      // Gentle centering to keep it on screen without crushing it
      graphRef.current.d3Force('center')?.strength(0.02);
    }
  }, [loading, data]);

  const processGraphData = (entries: GoodThing[]) => {
    const nodesMap = new Map<string, Node>();
    const linksMap = new Map<string, number>();

    entries.forEach(entry => {
      if (!entry.submitter?.trim() || entry.recipients.length === 0) return;

      if (!nodesMap.has(entry.submitter)) {
        nodesMap.set(entry.submitter, { 
          id: entry.submitter, 
          name: entry.submitter, 
          val: 1, 
          sent: 0, 
          received: 0 
        });
      }
      const submitterNode = nodesMap.get(entry.submitter)!;
      submitterNode.sent += 1;
      // Log scale for size
      submitterNode.val = 1 + Math.log2(submitterNode.sent + submitterNode.received + 1);

      entry.recipients.forEach(recipient => {
        if (!recipient?.trim()) return;

        if (!nodesMap.has(recipient)) {
          nodesMap.set(recipient, { 
            id: recipient, 
            name: recipient, 
            val: 1, 
            received: 0, 
            sent: 0 
          });
        }
        const recipientNode = nodesMap.get(recipient)!;
        recipientNode.received += 1;
        recipientNode.val = 1 + Math.log2(recipientNode.sent + recipientNode.received + 1);

        const linkKey = `${entry.submitter}|${recipient}`;
        linksMap.set(linkKey, (linksMap.get(linkKey) || 0) + 1);
      });
    });

    // Sort nodes by value so larger ones render on top
    const nodes = Array.from(nodesMap.values()).sort((a, b) => a.val - b.val);
    const links = Array.from(linksMap.entries()).map(([key, value]) => {
      const [source, target] = key.split('|');
      return { source, target, value };
    });

    setData({ nodes, links });
  };

  const handleNodeClick = useCallback((node: Node) => {
    setSelectedNode(node);
    
    // Smoother zoom
    if (node.x !== undefined && node.y !== undefined) {
        graphRef.current?.centerAt(node.x, node.y, 1000);
        graphRef.current?.zoom(2.5, 1000);
    }

    const connectedNodeIds = new Set<string>();
    const connectedLinkIds = new Set<string>();
    
    connectedNodeIds.add(node.id);
    
    data.links.forEach(link => {
      const sourceId = typeof link.source === 'object' ? (link.source as any).id : link.source;
      const targetId = typeof link.target === 'object' ? (link.target as any).id : link.target;

      if (sourceId === node.id || targetId === node.id) {
        connectedNodeIds.add(sourceId);
        connectedNodeIds.add(targetId);
        connectedLinkIds.add(`${sourceId}|${targetId}`);
      }
    });

    setHighlightNodes(connectedNodeIds);
    setHighlightLinks(connectedLinkIds);
  }, [data]);

  const handleBackgroundClick = () => {
    setSelectedNode(null);
    setHighlightNodes(new Set());
    setHighlightLinks(new Set());
    // Smooth reset
    graphRef.current?.zoomToFit(1000, 50);
  };

  const handleResetView = () => {
    handleBackgroundClick();
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);
    
    if (term.length > 2) {
      const foundNode = data.nodes.find(n => n.name.toLowerCase().includes(term.toLowerCase()));
      if (foundNode) {
        handleNodeClick(foundNode);
      }
    } else if (term.length === 0) {
      handleBackgroundClick();
    }
  };

  const openDetailsDialog = (type: 'sent' | 'received') => {
    if (!selectedNode) return;

    setDialogType(type);
    
    // Filter entries
    const filtered = rawEntries.filter(entry => {
      if (type === 'sent') {
        return entry.submitter === selectedNode.name;
      } else {
        return entry.recipients.includes(selectedNode.name);
      }
    });
    
    // Sort by date desc
    filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    setDialogEntries(filtered);
    setDialogOpen(true);
  };

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900 relative overflow-hidden">
      {/* Header / Controls */}
      <div className="absolute top-4 left-4 z-10 flex flex-col gap-4 w-80 pointer-events-none">
        <Card className="p-4 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md shadow-xl border-none pointer-events-auto ring-1 ring-black/5 dark:ring-white/10">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl shadow-lg shadow-pink-500/20">
              <Share2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-gray-900 dark:text-white text-lg tracking-tight">Good Things Network</h2>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {data.nodes.length} People • {data.links.length} Connections
              </p>
            </div>
          </div>

          <div className="relative group">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-pink-500 transition-colors" />
            <input
              type="text"
              placeholder="Search person..."
              value={searchTerm}
              onChange={handleSearch}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-100/50 dark:bg-gray-700/50 border-none rounded-xl text-sm focus:ring-2 focus:ring-pink-500/50 text-gray-900 dark:text-white placeholder-gray-500 transition-all"
            />
          </div>
        </Card>

        <AnimatePresence>
          {selectedNode && (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              <Card className="p-5 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md shadow-xl border-none pointer-events-auto ring-1 ring-black/5 dark:ring-white/10">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-bold text-xl text-gray-900 dark:text-white leading-tight">{selectedNode.name}</h3>
                    <div className="flex items-center gap-1.5 mt-1 text-xs font-medium text-amber-500">
                      <Zap className="w-3.5 h-3.5 fill-amber-500" />
                      <span>{highlightNodes.size - 1} Connections</span>
                    </div>
                  </div>
                  <button 
                    onClick={() => handleBackgroundClick()}
                    className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                  >
                    <Minimize2 className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <button 
                    onClick={() => openDetailsDialog('received')}
                    className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-3 rounded-xl border border-green-100 dark:border-green-900/30 hover:scale-105 transition-transform cursor-pointer text-left group"
                  >
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400 group-hover:text-green-700 dark:group-hover:text-green-300 transition-colors">{selectedNode.received}</div>
                    <div className="text-xs font-medium text-green-600/70 dark:text-green-400/70 uppercase tracking-wider mt-0.5">Received</div>
                  </button>
                  <button 
                    onClick={() => openDetailsDialog('sent')}
                    className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-3 rounded-xl border border-blue-100 dark:border-blue-900/30 hover:scale-105 transition-transform cursor-pointer text-left group"
                  >
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors">{selectedNode.sent}</div>
                    <div className="text-xs font-medium text-blue-600/70 dark:text-blue-400/70 uppercase tracking-wider mt-0.5">Sent</div>
                  </button>
                </div>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Reset View Button */}
      <div className="absolute top-4 right-4 z-10">
        <button
          onClick={handleResetView}
          className="p-3 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md shadow-lg rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 transition-all ring-1 ring-black/5 dark:ring-white/10"
          title="Reset View"
        >
          <Minimize2 className="w-5 h-5" />
        </button>
      </div>

      {/* Graph Container */}
      <div ref={containerRef} className="flex-1 w-full h-full cursor-move bg-gray-50 dark:bg-gray-900">
        {!loading && (
          <ForceGraph2D
            ref={graphRef}
            width={containerDimensions.width}
            height={containerDimensions.height}
            graphData={data}
            nodeLabel="name"
            nodeRelSize={6}
            linkColor={() => isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}
            linkWidth={(link: any) => highlightLinks.has(`${link.source.id}|${link.target.id}`) ? 1.5 : 0.5}
            linkDirectionalParticles={4} // Increased particle count
            linkDirectionalParticleWidth={(link: any) => highlightLinks.has(`${link.source.id}|${link.target.id}`) ? 6 : 0} // Bolden particles
            linkDirectionalParticleSpeed={0.002} // Slower speed (was 0.01)
            linkDirectionalParticleColor={(link: any) => {
              // Determine color based on direction relative to selected node
              if (selectedNode) {
                const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
                // If source is selected node, it's a "Sent" link -> Dark Yellow (Selected Color)
                if (sourceId === selectedNode.id) {
                  return SELECTED_COLOR; // #d97706
                }
                // If target is selected node, it's a "Received" link -> Yellow (Connected Color)
                return CONNECTED_COLOR; // #fbbf24
              }
              return '#fbbf24'; // Default to amber
            }}
            backgroundColor={isDarkMode ? '#0f172a' : '#f8fafc'}
            onNodeClick={handleNodeClick}
            onBackgroundClick={handleBackgroundClick}
            onNodeHover={(node: any) => {
              if (containerRef.current) {
                containerRef.current.style.cursor = node ? 'pointer' : 'move';
              }
            }}
            nodeCanvasObject={(node: any, ctx, globalScale) => {
              const label = node.name;
              const fontSize = 12/globalScale;
              const radius = node.val * 4;
              
              // Determine color state
              const isSelected = selectedNode?.id === node.id;
              const isConnected = highlightNodes.has(node.id);
              
              let fillStyle = NODE_COLOR;
              if (isSelected) fillStyle = SELECTED_COLOR;
              else if (isConnected) fillStyle = CONNECTED_COLOR;

              // Draw Shadow/Glow for important nodes
              if (isSelected || isConnected) {
                ctx.shadowColor = fillStyle;
                ctx.shadowBlur = 20;
              } else {
                ctx.shadowBlur = 0;
              }

              // Draw Node
              ctx.beginPath();
              ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI, false);
              ctx.fillStyle = fillStyle;
              ctx.fill();
              
              // Reset shadow
              ctx.shadowBlur = 0;

              // Draw Border for selected
              if (isSelected) {
                ctx.strokeStyle = '#fff';
                ctx.lineWidth = 2 / globalScale;
                ctx.stroke();
              }

              // Text Rendering Logic
              // Show text if node is selected, connected, or if zoomed in enough
              // Also show for very large nodes (hubs)
              const isHub = node.val > 3.5;
              const showText = isSelected || isConnected || globalScale > 1.5 || isHub;

              if (showText) {
                ctx.font = `${isSelected ? 'bold ' : ''}${fontSize}px Inter, system-ui, sans-serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                
                const textWidth = ctx.measureText(label).width;
                const fitsInside = textWidth < (radius * 1.8);

                if (fitsInside) {
                  // Draw inside
                  ctx.fillStyle = '#fff'; // Always white inside colored node
                  ctx.fillText(label, node.x, node.y);
                } else {
                  // Draw outside (below)
                  // Add background for readability
                  const padding = 3 / globalScale;
                  const bgHeight = fontSize + padding * 2;
                  const bgWidth = textWidth + padding * 4;
                  const bgY = node.y + radius + padding;
                  
                  ctx.fillStyle = TEXT_BG_COLOR;
                  ctx.beginPath();
                  ctx.roundRect(node.x - bgWidth/2, bgY, bgWidth, bgHeight, 4/globalScale);
                  ctx.fill();

                  ctx.fillStyle = TEXT_COLOR;
                  ctx.fillText(label, node.x, bgY + bgHeight/2);
                }
              }
            }}
            cooldownTicks={100}
            d3AlphaDecay={0.02}
            d3VelocityDecay={0.3}
            warmupTicks={100}
          />
        )}
      </div>

      {/* Legend / Info */}
      <div className="absolute bottom-4 right-4 z-10">
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md p-3 rounded-xl shadow-lg border border-white/20 dark:border-gray-700/30 text-xs font-medium text-gray-600 dark:text-gray-300 flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-pink-500 shadow-sm shadow-pink-500/50"></div>
            <span>Person</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-amber-600 shadow-sm shadow-amber-600/50"></div>
            <span>Selected</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-amber-400 shadow-sm shadow-amber-400/50"></div>
            <span>Connected</span>
          </div>
        </div>
      </div>

      {/* Details Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent 
          className="max-w-2xl h-[80vh] flex flex-col p-0 gap-0 overflow-hidden"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e: any) => {
            const target = e.detail.originalEvent.target as Element;
            if (target.closest('.reaction-popover-content')) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader className="p-6 pb-2 shrink-0">
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              {dialogType === 'received' ? (
                <>
                  <span className="text-green-600 dark:text-green-400">Received</span>
                  <span>by {selectedNode?.name}</span>
                </>
              ) : (
                <>
                  <span className="text-blue-600 dark:text-blue-400">Sent</span>
                  <span>by {selectedNode?.name}</span>
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 min-h-0 overflow-hidden relative">
            <TooltipProvider>
              <ScrollArea className="h-full w-full">
                <div className="p-6 pt-2 space-y-4">
                  {dialogEntries.length > 0 ? (
                    dialogEntries.map(entry => (
                      <GoodThingCard 
                        key={entry.id} 
                        goodThing={entry} 
                        onEdit={() => {
                           // Refresh data if needed
                           setDialogOpen(false);
                           window.location.reload(); 
                        }}
                        onDelete={() => {
                           setDialogOpen(false);
                           window.location.reload();
                        }}
                      />
                    ))
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      No entries found.
                    </div>
                  )}
                </div>
              </ScrollArea>
            </TooltipProvider>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GoodThingsNetwork;
